import { useEffect, useCallback } from 'react';
import {
  doc,
  collection,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  writeBatch,
  query,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthStore } from '../stores/authStore';
import { COLLECTIONS, SUBCOLLECTIONS } from '../lib/collections';
import {
  TOKEN_EARNINGS,
  DAILY_TOKEN_ALLOWANCE,
} from '../types/tokens';
import type { TokenLedgerEntry } from '../types/tokens';

function getProfileDocPath(accountId: string, profileId: string) {
  return `${COLLECTIONS.USERS}/${accountId}/${SUBCOLLECTIONS.PROFILES}/${profileId}`;
}

function getLedgerCollectionPath(accountId: string, profileId: string) {
  return `${COLLECTIONS.USERS}/${accountId}/${SUBCOLLECTIONS.PROFILES}/${profileId}/${SUBCOLLECTIONS.TOKEN_LEDGER}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isSameWeek(a: Date, b: Date): boolean {
  const startOfWeek = (d: Date) => {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
    return new Date(d.getFullYear(), d.getMonth(), diff);
  };
  return startOfWeek(a).getTime() === startOfWeek(b).getTime();
}

export function useTokens() {
  const accountId = useAuthStore((s) => s.accountId);
  const profile = useAuthStore((s) => s.profile);
  const setProfile = useAuthStore((s) => s.setProfile);

  // Subscribe to profile token balance changes
  useEffect(() => {
    if (!accountId || !profile) return;

    const profilePath = getProfileDocPath(accountId, profile.id);
    const profileRef = doc(db, profilePath);

    const unsubscribe = onSnapshot(profileRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data.tokenBalance) {
          setProfile({ ...profile, tokenBalance: data.tokenBalance });
        }
      }
    });

    return unsubscribe;
    // Only re-subscribe when account/profile identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, profile?.id]);

  // Check and perform daily reset if needed
  const checkDailyReset = useCallback(async () => {
    if (!accountId || !profile) return;

    const balance = profile.tokenBalance;
    if (!balance) return;

    const now = new Date();
    const lastReset = balance.lastDailyReset?.toDate();

    if (lastReset && isSameDay(lastReset, now)) return; // Already reset today

    const profilePath = getProfileDocPath(accountId, profile.id);
    const profileRef = doc(db, profilePath);

    const tier = balance.tier ?? 'active';
    const newAllowance = tier === 'expired' ? 0 : DAILY_TOKEN_ALLOWANCE;

    await updateDoc(profileRef, {
      'tokenBalance.currentBalance': newAllowance,
      'tokenBalance.dailyTokensUsed': 0,
      'tokenBalance.dailyTokensEarned': 0,
      'tokenBalance.lastDailyReset': serverTimestamp(),
    });
  }, [accountId, profile]);

  // Check and award weekly engagement bonus
  const checkWeeklyEngagement = useCallback(async () => {
    if (!accountId || !profile) return;

    const balance = profile.tokenBalance;
    if (!balance) return;

    const now = new Date();
    const lastWeeklyReset = balance.lastWeeklyReset?.toDate();

    // Reset weekly count if it's a new week
    if (lastWeeklyReset && !isSameWeek(lastWeeklyReset, now)) {
      const profilePath = getProfileDocPath(accountId, profile.id);
      const profileRef = doc(db, profilePath);
      await updateDoc(profileRef, {
        'tokenBalance.weeklyAppUses': 1,
        'tokenBalance.lastWeeklyReset': serverTimestamp(),
        'tokenBalance.lastWeeklyBonusAwarded': null,
      });
      return;
    }

    // Increment weekly uses
    const newUses = (balance.weeklyAppUses ?? 0) + 1;
    const profilePath = getProfileDocPath(accountId, profile.id);
    const profileRef = doc(db, profilePath);

    await updateDoc(profileRef, {
      'tokenBalance.weeklyAppUses': newUses,
    });

    // Award weekly bonuses if thresholds met and not already awarded
    const lastBonus = balance.lastWeeklyBonusAwarded?.toDate();
    const alreadyAwardedThisWeek =
      lastBonus && isSameWeek(lastBonus, now);

    if (!alreadyAwardedThisWeek) {
      const currentBalance = balance.currentBalance ?? 0;
      const batch = writeBatch(db);
      const ledgerPath = getLedgerCollectionPath(accountId, profile.id);
      let earned = 0;

      if (newUses >= 5) {
        earned = TOKEN_EARNINGS.WEEKLY_3_USES + TOKEN_EARNINGS.WEEKLY_5_USES_BONUS;
      } else if (newUses >= 3) {
        earned = TOKEN_EARNINGS.WEEKLY_3_USES;
      }

      if (earned > 0) {
        const ledgerRef = doc(collection(db, ledgerPath));
        batch.set(ledgerRef, {
          type: 'earned',
          amount: earned,
          feature: 'weekly_engagement',
          description: `Weekly engagement bonus (${newUses}+ uses)`,
          balanceAfter: currentBalance + earned,
          relatedPlanId: null,
          timestamp: serverTimestamp(),
        });
        batch.update(profileRef, {
          'tokenBalance.currentBalance': currentBalance + earned,
          'tokenBalance.lastWeeklyBonusAwarded': serverTimestamp(),
        });
        await batch.commit();
      }
    }
  }, [accountId, profile]);

  // Earn tokens for a specific action
  const earnTokens = useCallback(
    async (
      amount: number,
      feature: string,
      description: string,
      relatedPlanId: string | null = null,
    ) => {
      if (!accountId || !profile) return;

      const currentBalance = profile.tokenBalance?.currentBalance ?? 0;
      const newBalance = currentBalance + amount;

      const batch = writeBatch(db);
      const profilePath = getProfileDocPath(accountId, profile.id);
      const profileRef = doc(db, profilePath);
      const ledgerPath = getLedgerCollectionPath(accountId, profile.id);
      const ledgerRef = doc(collection(db, ledgerPath));

      batch.set(ledgerRef, {
        type: 'earned',
        amount,
        feature,
        description,
        balanceAfter: newBalance,
        relatedPlanId,
        timestamp: serverTimestamp(),
      });

      batch.update(profileRef, {
        'tokenBalance.currentBalance': newBalance,
        'tokenBalance.dailyTokensEarned':
          (profile.tokenBalance?.dailyTokensEarned ?? 0) + amount,
      });

      await batch.commit();
    },
    [accountId, profile],
  );

  // Spend tokens
  const spendTokens = useCallback(
    async (
      amount: number,
      feature: string,
      description: string,
    ) => {
      if (!accountId || !profile) return false;

      const currentBalance = profile.tokenBalance?.currentBalance ?? 0;
      if (currentBalance < amount) return false;

      const newBalance = currentBalance - amount;

      const batch = writeBatch(db);
      const profilePath = getProfileDocPath(accountId, profile.id);
      const profileRef = doc(db, profilePath);
      const ledgerPath = getLedgerCollectionPath(accountId, profile.id);
      const ledgerRef = doc(collection(db, ledgerPath));

      batch.set(ledgerRef, {
        type: 'spent',
        amount: -amount,
        feature,
        description,
        balanceAfter: newBalance,
        relatedPlanId: null,
        timestamp: serverTimestamp(),
      });

      batch.update(profileRef, {
        'tokenBalance.currentBalance': newBalance,
        'tokenBalance.dailyTokensUsed':
          (profile.tokenBalance?.dailyTokensUsed ?? 0) + amount,
      });

      await batch.commit();
      return true;
    },
    [accountId, profile],
  );

  // Check if user can spend a given amount
  const canSpend = useCallback(
    (amount: number): boolean => {
      const currentBalance = profile?.tokenBalance?.currentBalance ?? 0;
      return currentBalance >= amount;
    },
    [profile],
  );

  // Read recent ledger entries
  const getRecentLedger = useCallback(
    async (count = 10): Promise<TokenLedgerEntry[]> => {
      if (!accountId || !profile) return [];

      const ledgerPath = getLedgerCollectionPath(accountId, profile.id);
      const ledgerRef = collection(db, ledgerPath);
      const q = query(ledgerRef, orderBy('timestamp', 'desc'), limit(count));

      const snapshot = await getDocs(q);
      return snapshot.docs.map(
        (d) => ({ ...d.data() } as TokenLedgerEntry),
      );
    },
    [accountId, profile],
  );

  return {
    balance: profile?.tokenBalance?.currentBalance ?? 0,
    tier: profile?.tokenBalance?.tier ?? 'active',
    tokenBalance: profile?.tokenBalance ?? null,
    canSpend,
    earnTokens,
    spendTokens,
    checkDailyReset,
    checkWeeklyEngagement,
    getRecentLedger,
  };
}
